import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRefFromPath } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

import { db, storage } from "../../firebase";

const STATUS_FILTERS = [
  "all",
  "provisionally_approved",
  "approved",
  "rejected",
  "suspended",
];

const PAGE_STYLE = {
  padding: 16,
  color: "#e5e7eb",
};

const TOP_BAR_STYLE = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 16,
};

const TAB_BASE_STYLE = {
  border: "1px solid #334155",
  background: "#111827",
  color: "#cbd5e1",
  borderRadius: 999,
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 13,
};

const INPUT_STYLE = {
  minWidth: 260,
  background: "#0b1220",
  border: "1px solid #334155",
  color: "#e5e7eb",
  borderRadius: 8,
  padding: "9px 12px",
};

const CARD_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: 12,
};

const CARD_STYLE = {
  border: "1px solid #1f2937",
  background: "#0f172a",
  borderRadius: 12,
  padding: 14,
  display: "grid",
  gap: 8,
};

const MUTED_STYLE = { color: "#94a3b8", fontSize: 12, wordBreak: "break-all" };

const ACTION_BASE_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: "8px 12px",
  cursor: "pointer",
  color: "#fff",
  fontWeight: 600,
};

function shorten(value, start = 8, end = 6) {
  const str = String(value || "");
  if (str.length <= start + end + 3) return str || "—";
  return `${str.slice(0, start)}...${str.slice(-end)}`;
}

function getStatusBadgeStyle(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "approved") {
    return { background: "#064e3b", color: "#6ee7b7", border: "1px solid #047857" };
  }
  if (normalized === "rejected") {
    return { background: "#450a0a", color: "#fca5a5", border: "1px solid #b91c1c" };
  }
  if (normalized === "suspended") {
    return { background: "#422006", color: "#fdba74", border: "1px solid #c2410c" };
  }
  if (normalized === "provisionally_approved") {
    return { background: "#1e293b", color: "#93c5fd", border: "1px solid #334155" };
  }
  return { background: "#1f2937", color: "#d1d5db", border: "1px solid #374151" };
}

function formatCreatedAt(ts) {
  if (!ts) return "—";
  if (typeof ts.toDate === "function") return ts.toDate().toISOString();
  return String(ts);
}

function FirebaseStorageImagePreview({ storagePath }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const path = String(storagePath || "").trim();
    if (!path) return undefined;

    (async () => {
      try {
        const urlResolved = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setUrl(urlResolved);
      } catch (e) {
        console.error("VerificationPage image preview error:", storagePath, e);
        if (!cancelled) setUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  if (!storagePath) return <span>(no photo path)</span>;
  if (!url) return <span>loading preview…</span>;
  return <img src={url} alt="" width={120} />;
}

function FirebaseStorageVideoPreview({ storagePath }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const path = String(storagePath || "").trim();
    if (!path) return undefined;

    (async () => {
      try {
        const urlResolved = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setUrl(urlResolved);
      } catch (e) {
        console.error("VerificationPage video preview error:", storagePath, e);
        if (!cancelled) setUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  if (!storagePath) return <span>(no video path)</span>;
  if (!url) return <span>loading preview…</span>;
  return <video src={url} controls width={280} />;
}

export default function VerificationPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingRequestId, setWorkingRequestId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [search, setSearch] = useState("");

  const functions = getFunctions(undefined, "us-central1");

  useEffect(() => {
    setLoading(true);
    const base = collection(db, "verificationRequests");
    const q =
      selectedStatus === "all"
        ? query(base, orderBy("createdAt", "desc"), limit(25))
        : query(
            base,
            where("status", "==", selectedStatus),
            orderBy("createdAt", "desc"),
            limit(25)
          );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setRequests(list);
        setLoading(false);
      },
      (error) => {
        console.error("VerificationPage snapshot error:", error);
        setRequests([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [selectedStatus]);

  async function runReview(item, action) {
    if (
      action === "rejected" ||
      action === "suspended" ||
      action === "reject" ||
      action === "suspend"
    ) {
      const confirmed = window.confirm(
        t("admin.verification.confirm_review_action", { defaultValue: "Are you sure you want to {{action}} this request?", action })
      );
      if (!confirmed) return;
    }

    const reviewVerificationRequest = httpsCallable(
      functions,
      "reviewVerificationRequest"
    );
    setWorkingRequestId(item.id);

    try {
      await reviewVerificationRequest({
        requestId: item.id,
        action,
      });
      console.log("reviewVerificationRequest success:", {
        requestId: item.id,
        action,
      });
    } catch (error) {
      console.error("reviewVerificationRequest error:", error);
    } finally {
      setWorkingRequestId(null);
    }
  }

  const busy = workingRequestId != null;
  const term = String(search || "").trim().toLowerCase();
  const filteredRequests = requests.filter((item) => {
    if (!term) return true;
    const uid = String(item.uid || "").toLowerCase();
    const requestId = String(item.id || "").toLowerCase();
    return uid.includes(term) || requestId.includes(term);
  });

  return (
    <div style={PAGE_STYLE}>
      <header style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>{t("admin.verification.requests_title", { defaultValue: "Verification Requests" })}</h1>
        <p style={{ margin: "8px 0 0", color: "#94a3b8" }}>
          Review queue from Firestore (newest 25), with status filters and quick actions.
        </p>
        <p style={{ margin: "8px 0 0", color: "#cbd5e1" }}>
          Total shown: <strong>{filteredRequests.length}</strong>
        </p>
      </header>

      <div style={TOP_BAR_STYLE}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {STATUS_FILTERS.map((status) => {
            const active = selectedStatus === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setSelectedStatus(status)}
                style={{
                  ...TAB_BASE_STYLE,
                  background: active ? "#2563eb" : TAB_BASE_STYLE.background,
                  borderColor: active ? "#2563eb" : TAB_BASE_STYLE.border,
                  color: active ? "#fff" : TAB_BASE_STYLE.color,
                }}
              >
                {status === "all" ? "All" : status}
              </button>
            );
          })}
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.verification.search_placeholder", { defaultValue: "Search requestId or uid..." })}
          style={INPUT_STYLE}
        />
      </div>

      {loading ? (
        <div style={{ ...CARD_STYLE, textAlign: "center", color: "#94a3b8" }}>
          Loading verification requests...
        </div>
      ) : filteredRequests.length === 0 ? (
        <div style={{ ...CARD_STYLE, textAlign: "center", color: "#94a3b8" }}>
          No verification requests found
        </div>
      ) : (
        <div style={CARD_GRID_STYLE}>
          {filteredRequests.map((item) => {
            const isRunning = busy && workingRequestId === item.id;
            const badgeStyle = getStatusBadgeStyle(item.status);
            return (
              <article key={item.id} style={CARD_STYLE}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <strong>Request {shorten(item.id)}</strong>
                  <span
                    style={{
                      display: "inline-block",
                      borderRadius: 999,
                      padding: "3px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      ...badgeStyle,
                    }}
                  >
                    {String(item.status ?? "—")}
                  </span>
                </div>
                <div>
                  <strong>{t("admin.verification.request_id", { defaultValue: "requestId:" })}</strong> {shorten(item.id)}
                </div>
                <div>
                  <strong>{t("admin.fields.uid_colon", { defaultValue: "uid:" })}</strong> {shorten(item.uid)}
                </div>
                <div>
                  <strong>{t("admin.fields.created_at_colon", { defaultValue: "createdAt:" })}</strong> {formatCreatedAt(item.createdAt)}
                </div>
                <div>
                  <strong>{t("admin.verification.profile_photo", { defaultValue: "profile photo" })}</strong>
                  <div style={MUTED_STYLE}>{String(item.profilePhotoPath || "—")}</div>
                  <FirebaseStorageImagePreview storagePath={item.profilePhotoPath} />
                </div>
                <div>
                  <strong>{t("admin.verification.video_preview", { defaultValue: "video preview" })}</strong>
                  <div style={MUTED_STYLE}>{String(item.verificationVideoPath || "—")}</div>
                  <FirebaseStorageVideoPreview storagePath={item.verificationVideoPath} />
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => runReview(item, "approve")}
                    style={{
                      ...ACTION_BASE_STYLE,
                      background: busy ? "#14532d" : "#16a34a",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    {isRunning ? "Processing..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => runReview(item, "reject")}
                    style={{
                      ...ACTION_BASE_STYLE,
                      background: busy ? "#7f1d1d" : "#dc2626",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => runReview(item, "suspend")}
                    style={{
                      ...ACTION_BASE_STYLE,
                      background: busy ? "#78350f" : "#b45309",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    Suspend
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
